import { api } from './api';
import { errorSafe } from 'app/infrastructure/redux';
import { ApplicationState } from 'app/types-store';
import { FavouriteDefinition, ActionDispatching, Value, SelectionDefinition, DefaultSortOrder } from 'app/types-shared';
import * as actions from './action-types';
import { mapSortOptions, createSortOrder } from 'app/area/favourite/mapping';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { selectWorkingDefinition } from './selectors';
import { LoadFavouriteDefinitionParams, LoadFavouriteDefinitionEmbeddedParams } from './types';
import { FavouriteState } from './types-store';
import * as navigate from 'app/area/shared/navigation';
import { CreateFavouriteDefinitionFailure, LoadFavouriteDefinitionFailure } from 'app/area/favourite/action-types';
import { DisplayModal } from 'app/shell/action-types';
import { MODAL } from 'app/shell/modal-host-types';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { FavouriteReportDownload } from 'app/area/favourite/types';
import { reportTypes, searchTranslation } from './constants';
import { AutoCloseDuration, toastFactory } from 'app/infrastructure/notifications/toast-factory';
import produce from 'immer';
import { favouriteDefinitionsAreEqual } from 'app/area/favourite/compare';
import * as favDefTransforms from './favourite-definition-transforms';
import * as navigation from 'app/area/shared/navigation';
import * as companyReportActionCreators from 'app/area/company-report/actions';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { ReportId } from 'app/area/saved-reports/constants';
import { ReceiveCreateSpecialReport, RequestCreateSpecialReport } from 'app/area/company-report/action-types';

// eslint:disable-next-line: max-func-body-length
export const actionCreators = (instanceKey: string) => ({

  clearResults: (): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new actions.ClearResults(instanceKey));
  },

  createFavouriteDraftDefinition: (favouriteId: number, report: number, editSavedFavourite: boolean): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const favourite = await api.createDraftDefinition(favouriteId);
        navigation.toCaseListing(favourite.favourite.v, report, editSavedFavourite, editSavedFavourite, true);
      }
      catch (err) {
        dispatch(new CreateFavouriteDefinitionFailure(instanceKey, report, err));
        navigate.toStartupView();
      }
    },

  createCompanyReportDraftDefinition: (
    favouriteId: number,
    companyReportId: number,
    editSavedFavourite: boolean
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const favourite = await api.createDraftDefinition(favouriteId);
        navigation.toCompanyReportDraftFavourite(favourite.favourite.v, companyReportId, editSavedFavourite, true);
      }
      catch (err) {
        dispatch(new CreateFavouriteDefinitionFailure(instanceKey, companyReportId, err));
        navigate.toStartupView();
      }
    },

  loadReportCategory: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      dispatch(new actions.RequestReportCategory(instanceKey));

      const reportCategory = await api.reportCategory();
      const codes = reportCategory && reportCategory.rootCodes && reportCategory.rootCodes.map(codeItemToStoreValue);

      dispatch(new actions.ReceiveReportCategory(instanceKey, codes));
    }),

  loadSortOptions: (reportId: number): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      dispatch(new actions.RequestSortOptions(instanceKey));

      const state = getState().favourite as FavouriteState;
      const report = state.instances?.[instanceKey]?.workingDefinition.reports.find(r => r.report.v === reportId);

      const sortOptions = await api.sortOptions(reportId);
      const updatedSortOptions = mapSortOptions({
        ...sortOptions,
        defaultSortOrder: createSortOrder(report, sortOptions.defaultSortOrder)
      });

      dispatch(new actions.ReceiveSortOptions(instanceKey, updatedSortOptions));

    }),

  loadFavouriteDefinition: (params: LoadFavouriteDefinitionParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        const favouriteId = params.favouriteId;

        dispatch(new actions.RequestFavouriteDefinition(instanceKey));

        const definition = await api.definition(favouriteId);

        dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));

      } catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, params, err));
        navigate.toStartupView();
      }
    },

  loadFavouriteDefinitionEmbedded: (params: LoadFavouriteDefinitionEmbeddedParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        const favouriteId = params.favouriteId;

        dispatch(new actions.RequestFavouriteDefinition(instanceKey));

        const definition = await api.definition(favouriteId);

        dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));
      } catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, params, err));
      }
    },

  loadCompanyReportFavouriteDefinition: (params: LoadFavouriteDefinitionParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        const favouriteId = params.favouriteId;

        !!instanceKey && dispatch(new actions.RequestFavouriteDefinition(instanceKey));

        const definition = await api.definition(favouriteId);
        const companyReportId = definition?.reports?.filter(r => r.report?.v === ReportId.CompanyReport)[0]?.specialReport?.v;
        !!companyReportId && navigation.toSavedCompanyReport(favouriteId, companyReportId);

        !!instanceKey && dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));

      } catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, params, err));
        navigate.toStartupView();
      }
    },

  createCompanyReportDefinition: (
    companyReportId: number,
    favouriteId: number,
    eMailReport: boolean = false,
    userEmail: string
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const existingFavourite = !!favouriteId;
      try {
        let definition = null;
        const utcOffset = getState().app.settings.utcOffset;

        if (existingFavourite) {
          dispatch(new actions.RequestFavouriteDefinition(instanceKey));
          definition = await api.definition(favouriteId);

          const emailSubscribers = eMailReport && (definition.emailSubscribers || !definition.validState && userEmail);
          if (!!emailSubscribers) {
            definition = {
              ...definition,
              ...({ emailSubscribers: emailSubscribers })
            };
            dispatch(companyReportActionCreators.actionCreators.setEmail(emailSubscribers));
          }
          dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));
        }
        else {
          dispatch(new RequestCreateSpecialReport());
          const favouriteIdNew = await api.createCompanyReportDefinition(companyReportId, { utcOffset });
          definition = await api.definition(favouriteIdNew);
          definition = favDefTransforms.updateDefinitionWithReport(reportTypes.specialReport)(definition);
          definition = favDefTransforms.updateSelectedReport(
            definition, { specialReport: { v: companyReportId } }
          );
          definition = favDefTransforms.updateConditions(definition, []);
          if (eMailReport) {
            dispatch(companyReportActionCreators.actionCreators.setEmail(userEmail));
          }
          dispatch(new ReceiveCreateSpecialReport());
          navigation.toCompanyReportDraftFavourite(favouriteIdNew, companyReportId, false, true);
        }
      }
      catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, { favouriteId }, err));
        navigate.toStartupView();
      }
    },

  validateFavouriteFilters: (translation: any): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {

      dispatch(new ErrorNotification(translation.some_fields_are_filled_out_incorrectly));
      dispatch(new actions.ValidateFavouriteFilters(instanceKey));
    }),

  displayExportToZip: (): any => {
    return new DisplayModal(MODAL.exportToZip);
  },

  createSearchDefinition: (report: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const utcOffset = getState().app.settings.utcOffset;
        const favourite = await api.createSearchDefinition({ utcOffset });
        navigation.toCaseListing(favourite, report, true, false, true);
      }
      catch (err) {
        dispatch(new CreateFavouriteDefinitionFailure(instanceKey, report, err));
        navigate.toStartupView();
      }
    },

  downloadReport: (data: FavouriteReportDownload): ActionDispatching<any, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const toastMessage = toastFactory({ autoClose: AutoCloseDuration.Longer });
      try {
        dispatch(new actions.RequestDownloadReport(instanceKey));
        const areaState = getState().favourite;
        const translationDescription = createDescriptionSelector(areaState.texts);

        toastMessage.dispatch(translationDescription(searchTranslation.download_report_wait));

        const definition = await api.definition(data.favouriteId);

        const newDefinition = {
          ...definition,
          selectedReport: { v: data.reportId }
        };

        await api.downloadReport(newDefinition);

        dispatch(new actions.ReceiveDownloadReport(instanceKey));
        toastMessage.success(data.successMessage);

      } catch (err) {
        dispatch(new actions.FailDownloadReport(instanceKey));
        toastMessage.error(err.validationErrors || err.message);
      }
    },
  /**
   * Get url for opeining multiple suppliers in survey manager
   */
  getMulitpleSuppliersUrl: (): ActionDispatching<any, any> =>
  (async (dispatch: any, getState: any) => {
    try {
      dispatch(new actions.RequestUrlMultipleSuppliers(instanceKey));
      const multipleSuppliersUrl = await api.multipleSuppliersUrl();
      dispatch(new actions.ReceiveUrlMultipleSuppliers(instanceKey, multipleSuppliersUrl));
    }
    catch (err) {
      dispatch(new actions.FailReceiveUrlMultipleSuppliers(instanceKey, err));
    }
  }),

  getSearchRequiredUsers: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestSearchRequiredUsers(instanceKey));
      const searchRequiredUsers = await api.searchRequiredUsers();
      dispatch(new actions.ReceiveSearchRequiredUsers(instanceKey, searchRequiredUsers));
    }),

  transformWorkingDefinition: (transform: (definition: FavouriteDefinition) => FavouriteDefinition) =>
    errorSafe(async (dispatch: any, getState: any) => {
      const workingDefinition = selectWorkingDefinition(instanceKey)(getState());

      if (!workingDefinition || !transform) {
        return;
      }

      const newDefinition = produce(workingDefinition, transform);

      if (!favouriteDefinitionsAreEqual(workingDefinition, newDefinition)) {
        await api.saveFavouriteDefinition(newDefinition);
        dispatch(new actions.UpdateWorkingDefinition(instanceKey, newDefinition));
      }
    }),

  setWorkingDefinitionSelectedReport: (reportId: number) =>
    actionCreators(instanceKey).transformWorkingDefinition(def =>
      favDefTransforms.updateDefinitionWithReport(reportId)(def)
    ),

  setWorkingDefinitionSortOptions: (reportId: number, sortOrder: DefaultSortOrder[]) =>
    actionCreators(instanceKey).transformWorkingDefinition(def =>
      favDefTransforms.updateDefinitionWithSort(reportId, sortOrder)(def)
    ),

  setWorkingDefinitionQuery: (query: string) =>
    actionCreators(instanceKey).transformWorkingDefinition(def =>
      favDefTransforms.updateDefinitionWithQuery(query)(def)
    ),

  setWorkingDefinitionAggregation:
    (
      aggregationDomain: Value,
      aggregationType: Value,
      uomSetDomain: Value,
      clearSections?: { target?: boolean; rollingAverage?: boolean; selections?: boolean }
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => {
          if (clearSections?.target) {
            def = favDefTransforms.transformSelectedReport({ targetApp: undefined })(def);
          }
          if (clearSections?.rollingAverage) {
            def = favDefTransforms.transformSelectedReport({ rollingAverage: undefined })(def);
          }
          if (clearSections?.selections) {
            def = favDefTransforms.transformSelectedReport({ selections: [] })(def);
          }
          return favDefTransforms.updateAggregation(def, aggregationDomain, aggregationType, uomSetDomain);
        }
      ),
  setWorkingDefinitionTargetApplication:
    (
      targetApplication: Value
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateTarget(def, targetApplication)
      ),
  setWorkingDefinitionExposure:
    (
      exposureApplication: Value,
      exposureFactor?: number,
      exposurePercent?: number
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => {
          if (def.exposureApplication?.v !== exposureApplication?.v) {
            // If exposure application has changed, rolling average and selections should be cleared
            def = favDefTransforms.transformSelectedReport({ rollingAverage: undefined, selections: [] })(def);
          }
          return favDefTransforms.updateExposure(def, exposureApplication, exposureFactor ?? 0, exposurePercent ?? 0);
        }
      ),
  setWorkingDefinitionSelections:
    (
      selectedGroupByFields: SelectionDefinition[],
      rollingAverage?: number
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateGroupBy(def, selectedGroupByFields, rollingAverage)
      ),
  setWorkingDefinitionLayoutAttributes:
    (
      attributes: { [key: string]: string | boolean | number }[],
      layoutType: number,
      layoutTypeContext: number
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateFavouriteLayoutAttributes(def, attributes, layoutType, layoutTypeContext)
      ),

  setWorkingDefinitionLayoutType:
    (
      layoutType: Value
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateLayoutType(def, layoutType)
      ),

  setWorkingDefinitionDrillDownReport:
    (
      drillDownReport: Value
    ) =>
      actionCreators(instanceKey).transformWorkingDefinition(
        def => favDefTransforms.updateDrillDownReport(def, drillDownReport)
      ),

  loadDashboardFavouriteDefinition: (params: LoadFavouriteDefinitionParams): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        const favouriteId = params.favouriteId;

        dispatch(new actions.RequestFavouriteDefinition(instanceKey));

        const definition = await api.definition(favouriteId);
        const dashboardType = definition?.reports?.filter(r => r.report?.v === ReportId.Dashboard)[0]?.dashboard?.v;
        !!dashboardType && navigation.toSavedDashboard(favouriteId, dashboardType);

        dispatch(new actions.ReceiveFavouriteDefinition(instanceKey, definition, favouriteId));

      } catch (err) {
        dispatch(new LoadFavouriteDefinitionFailure(instanceKey, params, err));
        navigate.toStartupView();
      }
    },

  createDashboardDraftDefinition: (
    favouriteId: number,
    dashboardType: number,
    editSavedFavourite: boolean
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const favourite = await api.createDraftDefinition(favouriteId);
        navigation.toDashboardDraftFavourite(favourite.favourite.v, dashboardType, editSavedFavourite, true);
      }
      catch (err) {
        dispatch(new CreateFavouriteDefinitionFailure(instanceKey, dashboardType, err));
        navigate.toStartupView();
      }
    }
});
